import { useNavigate } from 'react-router-dom';

interface Offer {
  id: string;
  title: string;
  description: string;
  discount: string;
  code: string;
  validUntil: string;
  type: 'percentage' | 'flat' | 'freebie';
}

export default function SpecialOffers() {
  const navigate = useNavigate();
  
  const offers: Offer[] = [
    {
      id: '1',
      title: 'Welcome Back!',
      description: 'Get 20% off on your next purchase',
      discount: '20% OFF',
      code: 'WELCOME20',
      validUntil: '2024-03-31',
      type: 'percentage'
    },
    {
      id: '2',
      title: 'Flat Discount',
      description: 'Save ₹100 on orders above ₹500',
      discount: '₹100 OFF',
      code: 'SAVE100',
      validUntil: '2024-02-28',
      type: 'flat'
    },
    {
      id: '3',
      title: 'Free Shipping',
      description: 'Free delivery on all orders',
      discount: 'FREE SHIP',
      code: 'FREESHIP',
      validUntil: '2024-02-15',
      type: 'freebie'
    },
    {
      id: '4',
      title: 'Bundle Deal',
      description: 'Buy 2 products and get 15% off',
      discount: '15% OFF',
      code: 'BUNDLE15',
      validUntil: '2024-03-15',
      type: 'percentage'
    }
  ];

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const getOfferColor = (type: Offer['type']) => {
    switch (type) {
      case 'percentage':
        return 'from-emerald-500 to-teal-600';
      case 'flat':
        return 'from-amber-500 to-orange-600';
      case 'freebie':
        return 'from-purple-500 to-pink-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Special Offers</h2>
        <p className="text-sm text-gray-600">Exclusive deals just for you</p>
      </div>

      <div className="space-y-4">
        {offers.map((offer) => (
          <div key={offer.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className={`bg-gradient-to-r ${getOfferColor(offer.type)} p-4 text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">{offer.title}</h3>
                  <p className="text-sm opacity-90 mt-1">{offer.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{offer.discount}</div>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-600">CODE:</span>
                  <code className="px-3 py-1 bg-gray-100 text-emerald-600 font-bold rounded text-sm">
                    {offer.code}
                  </code>
                </div>
                <button
                  onClick={() => copyCode(offer.code)}
                  className="p-2 text-gray-600 hover:text-emerald-600 cursor-pointer"
                  title="Copy code"
                >
                  <i className="ri-file-copy-line w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
              
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                <span>Valid until {new Date(offer.validUntil).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric' 
                })}</span>
              </div>

              <button
                onClick={() => navigate('/ecom')}
                className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shop Now
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-6 border border-emerald-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
            <i className="ri-gift-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 mb-2">Loyalty Rewards</h3>
            <p className="text-sm text-gray-700 mb-3">
              You have earned 450 points! Redeem them for exclusive discounts and free products.
            </p>
            <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer text-sm whitespace-nowrap">
              View Rewards
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
